<template>
  <div class="min-h-screen bg-background text-foreground">
    <!-- Scroll to Top Button -->
    <button
      v-show="showScrollTop"
      @click="scrollToTop"
      class="fixed bottom-8 right-8 z-50 p-3 rounded-full bg-primary text-primary-foreground shadow-lg hover:bg-primary/90 focus:outline-hidden focus:ring-2 focus:ring-primary/30 transition-all duration-300"
      aria-label="Scroll to top"
    >
      <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 10l7-7m0 0l7 7m-7-7v18" />
      </svg>
    </button>



    <!-- Wrap entire content in VehicleFilters -->
    <VehicleFilters ref="vehicleFiltersRef">
      <!-- Desktop Header Controls -->
      <header class="flex items-center justify-between mb-6 sticky top-0 z-30 py-4 bg-background/80 backdrop-blur-md">
        <div class="flex items-center space-x-4 hidden lg:flex">
          <!-- Sort Dropdown -->
          <div class="relative">
            <select v-model="sortOption" @change="applySortOption" class="appearance-none bg-muted/50 border border-border/50 rounded-lg px-4 py-2 pr-8 text-sm font-medium text-foreground hover:bg-muted focus:outline-none focus:ring-2 focus:ring-primary/30 transition-colors">
              <option value="relevance">Most Relevant</option>
              <option value="price_asc">Price (Low to High)</option>
              <option value="price_desc">Price (High to Low)</option>
              <option value="year_desc">Newest First</option>
              <option value="year_asc">Oldest First</option>
              <option value="kms_asc">Lowest Kms</option>
            </select>
            <ChevronDownIcon class="absolute right-2 top-1/2 -translate-y-1/2 h-5 w-5 text-muted-foreground pointer-events-none" />
          </div>
          <!-- Display Mode Toggle -->
          <Button @click="toggleViewMode" variant="outline" size="icon" aria-label="Toggle view mode">
            <Squares2X2Icon v-if="viewMode === 'list'" class="h-5 w-5" />
            <ListBulletIcon v-else class="h-5 w-5" />
          </Button>
          <!-- Pagination/Virtual Scroll Toggle -->
          <Button @click="toggleScrollMode" variant="outline" size="icon" title="Toggle between pagination and virtual scroll" aria-label="Toggle scroll mode">
            <template v-if="useVirtualScroll">
              <svg class="h-5 w-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <rect x="3" y="3" width="18" height="18" rx="2" /> <line x1="9" y1="3" x2="9" y2="21" /> <line x1="15" y1="3" x2="15" y2="21" /> <line x1="3" y1="9" x2="21" y2="9" /> <line x1="3" y1="15" x2="21" y2="15" />
              </svg>
            </template>
            <template v-else>
              <svg class="h-5 w-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <rect x="2" y="2" width="20" height="8" rx="2" /> <rect x="2" y="14" width="20" height="8" rx="2" />
              </svg>
            </template>
          </Button>
        </div>

        <div class="flex items-center space-x-4">
          
          <!-- Search Input -->
          <div class="relative">
            <Input
              v-model="searchQuery"
              @input="debouncedApplySearch"
              @keyup.enter="applySearchNow"
              type="text"
              placeholder="Search vehicles..."
              class="w-100 pl-10 pr-16 py-2"
            />
            <MagnifyingGlassIcon class="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-muted-foreground" />
            <button
              v-if="searchQuery.trim().length > 0"
              @click="clearSearch"
              class="absolute right-9 top-1/2 -translate-y-1/2 p-1 rounded-md text-muted-foreground hover:text-foreground hover:bg-muted/50 transition-colors"
              title="Clear search" aria-label="Clear search"
            >
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="h-4 w-4"><path d="M18 6 6 18" /><path d="m6 6 12 12" /></svg>
            </button>
          </div>
        </div>
      </header>

      <!-- Loading State -->
      <div v-if="vehiclesStore.isLoading" class="text-center py-10">Loading vehicles...</div>
      <!-- Error State -->
      <div v-else-if="vehiclesStore.error" class="text-center py-10 text-red-500">Error: {{ vehiclesStore.error }}</div>

      <!-- Vehicle Grid/List Area -->
      <div v-else class="w-full">
        <!-- Empty Search Results -->
        <EmptySearchResults
          v-if="sortedVehicles.length === 0"
          :search-query="searchQuery"
          @clear-search="clearSearch"
          @clear-all-filters="handleClearAllFilters"
        />

        <!-- Pagination Mode (when results exist) -->
        <template v-else-if="!useVirtualScroll">
          <div :class="[
            'grid gap-2',
            viewMode === 'grid' ? 'grid-cols-[repeat(auto-fill,minmax(min(100%,320px),1fr))] auto-rows-fr' : 'grid-cols-1'
          ]">
            <VehicleCard
              v-for="vehicle in paginatedVehicles"
              :key="vehicle.id"
              :vehicle="vehicle"
              :layout="viewMode"
            />
          </div>
          <!-- Pagination Controls -->
          <div v-if="totalPages > 1" class="flex justify-center items-center space-x-2 py-8">
            <Button @click="handlePageChange(currentPage - 1)" :disabled="currentPage === 1" variant="outline">Previous</Button>
            <span class="text-sm text-muted-foreground">Page {{ currentPage }} of {{ totalPages }}</span>
            <Button @click="handlePageChange(currentPage + 1)" :disabled="currentPage === totalPages" variant="outline">Next</Button>
          </div>
        </template>

        <!-- Virtual Scroller Mode (when results exist) -->
        <template v-else>
          <div class="virtual-scroller-wrapper">
            <RecycleScroller
              class="scroller"
              :class="{ 'grid gap-6': viewMode === 'grid' }"
              :items="sortedVehicles"
              :item-size="getItemSize"
              :grid-items="viewMode === 'grid' ? getGridItemsCount : undefined"
              key-field="id"
              v-slot="{ item }"
              :buffer="500"
              :prerender="10"
            >
              <VehicleCard :vehicle="item" :layout="viewMode" />
            </RecycleScroller>
          </div>
        </template>
      </div>
    </VehicleFilters>

    <!-- VehicleQuickView Component -->
    <VehicleQuickView
      v-if="quickViewVehicleId"
      :vehicle-id="quickViewVehicleId"
      :is-open="isQuickViewOpen"
      @close="closeQuickView"
      :filtered-vehicle-ids="filteredVehicleIds"
    />

    <!-- Mobile Filters Sheet -->
     <Sheet v-model:open="isDrawerOpen">
       <SheetContent side="left" class="w-full sm:max-w-md flex flex-col p-0">
         <SheetHeader class="p-4 border-b border-border">
           <SheetTitle class="text-2xl">Filters</SheetTitle>
            <!-- Add Clear All button -->
            <Button v-if="actualActiveFilterCount > 0" @click="handleClearAllFilters" variant="link" size="sm" class="absolute right-4 top-4 text-primary">Clear all</Button>
         </SheetHeader>
         <div class="flex-1 overflow-hidden">
            <ScrollArea class="h-[calc(100vh-10rem)] scrollarea-hide-scrollbar" viewportClass="h-full">
              <div class="p-4">
                <VehicleFilters />
              </div>
            </ScrollArea>
         </div>
         <SheetFooter class="p-4 border-t border-border bg-background">
           <Button @click="isDrawerOpen = false" class="w-full text-base py-6">Show {{ filteredVehiclesCount }} Results</Button>
         </SheetFooter>
       </SheetContent>
     </Sheet>

    <!-- AI Assistant Dialog Placeholder -->
    <!--
    <Dialog v-model:open="isAIAssistantOpen"> ... </Dialog>
    -->
  </div>
</template>

<script setup lang="ts">
import { ref, computed, watch, onMounted, nextTick, onBeforeUnmount } from 'vue'
import { useVehiclesStore } from '~/stores/vehicles'
import { useRoute, useRouter, navigateTo } from '#imports'
import { useDebounceFn } from '@vueuse/core'
// import { RecycleScroller } from 'vue-virtual-scroller' // Commented out - missing type declarations
import 'vue-virtual-scroller/dist/vue-virtual-scroller.css'
// import { calculateWeeklyPayment } from '~/utils/finance' // Rely on auto-import

// Components
// import VehicleFilters from '~/components/VehicleFilters.vue' // Rely on auto-import
// import VehicleCard from '~/components/VehicleCard.vue' // Rely on auto-import
// import VehicleQuickView from '~/components/VehicleQuickView.vue' // Rely on auto-import
// import EmptySearchResults from '~/components/EmptySearchResults.vue' // Rely on auto-import
// import { Button } from '~/components/ui/button' // Rely on auto-import
// import { Input } from '~/components/ui/input' // Rely on auto-import
// import {
//    Sheet, SheetContent, SheetHeader, SheetTitle, SheetFooter
// } from '~/components/ui/sheet' // Rely on auto-import
import {
  FunnelIcon, ChevronDownIcon, Squares2X2Icon, ListBulletIcon,
  MagnifyingGlassIcon, HeartIcon, ShoppingCartIcon, XMarkIcon
} from '@heroicons/vue/24/outline'
// import { ScrollArea } from '~/components/ui/scroll-area' // Rely on auto-import

// Composables & Stores (ensure these imports remain commented)
// import { useVehicleQuickView } from '~/composables/useVehicleQuickView'
// import { useVehiclesPageFilters } from '~/stores/vehiclesPageFilters' 
// import { useVehicleScrollPosition } from '~/composables/useVehicleScrollPosition'

const route = useRoute()
const router = useRouter()
const vehiclesStore = useVehiclesStore()

// Get the vehicles page filters store instance - UNCOMMENT THIS USAGE
// const vehiclesPageFiltersStore = useVehiclesPageFilters(); // Commented out - composable not found

// Initialize scroll position management - UNCOMMENT THIS USAGE
const { restoreScrollPosition } = useVehicleScrollPosition();

// --- UI State ---
const showScrollTop = ref(false)
const isFiltersVisible = ref(true);
const isDrawerOpen = ref(false);
const viewMode = ref<'grid' | 'list'>('grid')
const useVirtualScroll = ref(false) // Default to pagination
const sortOption = ref('relevance')
const searchQuery = ref('')
const isMounted = ref(false) // For checking favorite/cart status client-side
const vehicleFiltersRef = ref() // Ref for filter component (if needed)
const isUnmounting = ref(false) // Flag to prevent watcher action during unmount

// --- Quick View ---
const { quickViewVehicleId, isQuickViewOpen, openQuickView, closeQuickView } = useVehicleQuickView();
const filteredVehicleIds = computed(() => vehiclesStore.filteredVehicles.map((v: any) => v.id));

// --- Data Fetching & Store ---
// Use useAsyncData for SSR optimization
const { data: vehiclesData } = await useAsyncData(
  'vehicles-list',
  () => vehiclesStore.fetchVehicles(),
  {
    // Only fetch if data isn't already loaded
    getCachedData(key) {
      if (vehiclesStore.isDataLoaded) {
        return vehiclesStore.vehicles
      }
    }
  }
)

// --- Filtering & Sorting ---
const actualActiveFilterCount = computed(() => {
  // Simple count of active filter keys (excluding pagination/quickview)
  return Object.keys(vehiclesStore.activeFilters).filter(k => !['page', 'quickView'].includes(k)).length;
});

const filteredVehiclesCount = computed(() => {
    return vehiclesStore.filteredVehicles.length;
});

const sortedVehicles = computed(() => {
  const vehiclesToSort = [...vehiclesStore.filteredVehicles];
  switch (sortOption.value) {
          case 'price_asc':
      // Ensure values are numbers before subtracting
      return vehiclesToSort.sort((a, b) => (Number(a.price ?? 0)) - (Number(b.price ?? 0)));
          case 'price_desc':
      // Ensure values are numbers before subtracting
      return vehiclesToSort.sort((a, b) => (Number(b.price ?? 0)) - (Number(a.price ?? 0)));
          case 'kms_asc':
        // Ensure values are numbers before subtracting
        return vehiclesToSort.sort((a, b) => (Number(a.kms ?? 0)) - (Number(b.kms ?? 0)));
    case 'year_desc': {
        const getYear = (v: any) => typeof v.year === 'number' ? v.year : Number(v.year?.value?.[0] ?? 0);
        // Ensure values are numbers before subtracting
        return vehiclesToSort.sort((a, b) => Number(getYear(b)) - Number(getYear(a)));
    }
    case 'year_asc': {
        const getYear = (v: any) => typeof v.year === 'number' ? v.year : Number(v.year?.value?.[0] ?? 0);
         // Ensure values are numbers before subtracting
        return vehiclesToSort.sort((a, b) => Number(getYear(a)) - Number(getYear(b)));
    }
    case 'relevance':
    default:
      return vehiclesToSort;
  }
});

// --- Pagination ---
const vehiclesPerPage = ref(16) // Items per page
const currentPage = ref(Number(route.query.page) || 1)

const totalPages = computed(() => {
  return Math.ceil(sortedVehicles.value.length / vehiclesPerPage.value)
});

const paginatedVehicles = computed(() => {
  if (useVirtualScroll.value) return []; // Don't paginate in virtual scroll mode
  const start = (currentPage.value - 1) * vehiclesPerPage.value;
  const end = start + vehiclesPerPage.value;
  return sortedVehicles.value.slice(start, end);
});

// --- Virtual Scroll ---
const getItemSize = computed(() => {
  // Simplified item height logic - adjust as needed
  return viewMode.value === 'list' ? 150 : 450; // Adjusted grid height estimate
});

const getGridItemsCount = computed(() => {
  if (viewMode.value !== 'grid' || typeof window === 'undefined') return undefined; // Let RecycleScroller handle if not grid
  const width = window.innerWidth;
  if (width < 640) return 1;
  if (width < 768) return 2;
  if (width < 1024) return 3;
  if (width < 1280) return 4;
  return 5;
});

// --- URL Synchronization ---
watch(() => route.query, (newQuery) => {
  // Prevent action if component is unmounting
  if (isUnmounting.value) {
    // console.log('[vehicles/index.vue] Watcher route.query: Skipped due to isUnmounting flag.'); // DEV LOG
    return;
  }

  // Update current page from URL
  currentPage.value = Number(newQuery.page) || 1;

  // Update sort option from URL (if implemented)
  // sortOption.value = newQuery.sort || 'relevance';

  // Update search query from URL
  searchQuery.value = (newQuery.search_keywords as string) || '';

  // Apply filters (excluding page, sort etc.)
  const filtersToApply = { ...newQuery };
  delete filtersToApply.page;
  delete filtersToApply.sort; // Example if sort was in query
  delete filtersToApply.quickView; // Quick view is handled separately

  // Check if filtersToApply is actually empty after removing non-filter params
  const hasActualFilters = Object.keys(filtersToApply).length > 0;

  // Only apply if filters actually changed AND there are filters to apply
  if (hasActualFilters && JSON.stringify(filtersToApply) !== JSON.stringify(vehiclesStore.activeFilters)) {
      //console.log('[vehicles/index.vue] Watcher route.query: Applying filters from URL:', JSON.stringify(filtersToApply)); // DEV LOG
      vehiclesStore.applyFilters(filtersToApply);
  } else if (!hasActualFilters && Object.keys(vehiclesStore.activeFilters).length > 0) {
      // If URL has no filters, but store still does, clear the store
      // This handles cases like clicking 'Clear All' which only updates URL
      //console.log('[vehicles/index.vue] Watcher route.query: URL is empty, clearing store filters.'); // DEV LOG
      vehiclesStore.applyFilters({});
  } else {
       //console.log('[vehicles/index.vue] Watcher route.query: No filter changes detected or filters are empty.'); // DEV LOG
  }
}, { immediate: true, deep: true });

watch(() => vehiclesStore.activeFilters, (newFilters) => {
  // Reset page to 1 when filters change
  currentPage.value = 1;
  updateUrlQuery(newFilters);
  // Scroll to top after filters applied
  window.scrollTo({ top: 0, behavior: 'smooth' });
}, { deep: true });

watch(sortOption, () => {
    // Reset to page 1 and update URL when sorting changes
    currentPage.value = 1;
    updateUrlQuery(vehiclesStore.activeFilters);
    // Scroll to top after sort applied
    window.scrollTo({ top: 0, behavior: 'smooth' });
});

// Helper to update URL query, preserving page, sort, quickView
const updateUrlQuery = (filters: Record<string, any>) => {
    if (route.path !== '/vehicles') return;

    const currentQuery = { ...route.query }; // Start with current query
    const queryForRouter: Record<string, string | undefined> = {}; // New object for router

    // Process filters for the router query
    for (const key in filters) {
        const value = filters[key];
        if (value === null || value === undefined || value === '') continue;

        if (Array.isArray(value)) {
            if (value.length > 0) {
                queryForRouter[key] = value.join(','); // Join arrays into comma-separated strings
            }
        } else if (typeof value === 'object' && value !== null && 'min' in value && 'max' in value) {
            // Handle range objects (assuming simple min/max for this context)
            queryForRouter[`${key}_min`] = String(value.min);
            queryForRouter[`${key}_max`] = String(value.max);
        } else if (typeof value === 'boolean') {
             if (value === true) { // Only add if true
                queryForRouter[key] = 'true';
            }
        } else {
            queryForRouter[key] = String(value); // Convert other types to string
        }
    }

    // Add/preserve non-filter params
    if (currentPage.value > 1) queryForRouter.page = String(currentPage.value);
    if (sortOption.value !== 'relevance') queryForRouter.sort = sortOption.value;
    if (isQuickViewOpen.value && quickViewVehicleId.value) queryForRouter.quickView = quickViewVehicleId.value;

    // Remove empty filter values (already handled by initial loop, but good practice)
    Object.keys(queryForRouter).forEach(key => {
        if (queryForRouter[key] === null || queryForRouter[key] === undefined || queryForRouter[key] === '') {
            delete queryForRouter[key];
        }
    });

    // Only replace if the relevant parts of the query have changed
    // Compare the processed queryForRouter with the current route query
    const normalizedNewQuery = JSON.stringify(queryForRouter);
    const normalizedCurrentQuery = JSON.stringify(currentQuery);

    if (normalizedNewQuery !== normalizedCurrentQuery) {
        router.replace({ query: queryForRouter }); // Pass the processed query object
    }
};


// --- Event Handlers ---
const handlePageChange = (newPage: number) => {
  if (newPage >= 1 && newPage <= totalPages.value) {
    currentPage.value = newPage;
    updateUrlQuery(vehiclesStore.activeFilters); // Update URL
    // Scroll to top of list or handle smoothly
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }
};

const applySortOption = () => {
    // handlePageChange(1); // Already handled by watcher
};

const toggleFilters = () => {
    isFiltersVisible.value = !isFiltersVisible.value;
};

const toggleViewMode = () => {
  viewMode.value = viewMode.value === 'grid' ? 'list' : 'grid'
  if (process.client) localStorage.setItem('vehicleViewMode', viewMode.value);
};

const toggleScrollMode = () => {
  useVirtualScroll.value = !useVirtualScroll.value;
  if (process.client) localStorage.setItem('vehicleScrollMode', useVirtualScroll.value ? 'virtual' : 'pagination');
  // Reset pagination when switching modes
  if (!useVirtualScroll.value) currentPage.value = 1;
};

const applySearchNow = () => {
    updateUrlQuery({ ...vehiclesStore.activeFilters, search_keywords: searchQuery.value.trim() || undefined });
    // Scroll to top after search applied
    window.scrollTo({ top: 0, behavior: 'smooth' });
}
const debouncedApplySearch = useDebounceFn(applySearchNow, 500);

const clearSearch = () => {
  searchQuery.value = '';
  applySearchNow();
};

const handleClearAllFilters = () => {
    // Logic is now handled by the VehicleFilters component communicating via URL
    // Just need to push an empty query
    router.push({ query: {} });
};

const navigateToVehicle = (vehicleId: string) => {
  const vehicle = vehiclesStore.vehicles.find(v => v.id === vehicleId)
  if (vehicle) {
    const slug = vehicle.slug || 'vehicle'; // Basic slug fallback
    navigateTo(`/vehicle-for-sale/${vehicle?.stockid || vehicleId}/${slug}`);
  }
};

const isFavorite = (id: string) => {
    return isMounted.value && vehiclesStore.isFavorite(id);
};

const getWeeklyPriceDisplay = (perweek: any, price: number): string | null => {
   // First try to use the perweek value if it's already calculated
   const weeklyValue = Number(perweek ?? 0);
   if (weeklyValue > 0) return weeklyValue.toLocaleString();

   // If perweek is not available, use the same calculation as the filter logic
   // This ensures consistency between filtering and display
   if (price > 0) {
     // Use the imported calculateWeeklyPayment function from utils/finance
     const calculated = calculateWeeklyPayment(price);
     return calculated > 0 ? calculated.toLocaleString() : null;
   }

   // Last resort fallback calculation (should not reach here)
   const calculated = Math.round(price / 52);
   return calculated > 0 ? calculated.toLocaleString() : null;
};

const isNewArrival = (vehicle: any) => {
   // Simplified - Needs actual date logic
   return Math.random() > 0.8; // Placeholder
};

const scrollToTop = () => {
  window.scrollTo({ top: 0, behavior: 'smooth' });
};

// --- Lifecycle Hooks ---
onMounted(async () => {
  isMounted.value = true;
  // Restore scroll position after DOM updates
  await nextTick();
  if (typeof restoreScrollPosition === 'function') { // Guard for safety
    restoreScrollPosition(); // Call with no arguments
  }

  // Initialize the page-specific filters store
  // if (vehiclesPageFiltersStore && typeof vehiclesPageFiltersStore.setupPageFilters === 'function') {
  //   await vehiclesPageFiltersStore.setupPageFilters(); // Commented out - composable not found
  // }

  // Load preferences for viewMode and useVirtualScroll from localStorage
  const savedViewMode = localStorage.getItem('vehiclesViewMode');
  if (savedViewMode) viewMode.value = savedViewMode as 'grid' | 'list';
  const savedScrollMode = localStorage.getItem('vehiclesScrollMode');
  if (savedScrollMode) useVirtualScroll.value = savedScrollMode === 'virtual';

  // Add scroll listener for scrollTop button
  window.addEventListener('scroll', handleScroll);
});

onBeforeUnmount(() => {
  isUnmounting.value = true;
  window.removeEventListener('scroll', handleScroll);
  // Persist preferences
  localStorage.setItem('vehiclesViewMode', viewMode.value);
  localStorage.setItem('vehiclesScrollMode', useVirtualScroll.value ? 'virtual' : 'pagination');
});

const handleScroll = () => {
    showScrollTop.value = window.scrollY > 300;
};

// --- SEO ---
// useSeoMeta({ ... }); // Add SEO meta if needed

</script>

<style scoped>


/* Ensure cards have consistent height in grid mode for pagination */
.grid > .group {
   height: 100%;
}

/* Virtual Scroller Wrapper height */
.virtual-scroller-wrapper {
  width: 100%;
  /* Adjust height calculation based on sticky headers/footers */
  height: calc(100vh - 220px); /* Example adjustment */
  min-height: 400px;
}
.scroller {
  height: 100%;
  width: 100%;
  overflow-y: auto;
}

/* List Item Styles */
.list-item {
  padding: 0.5rem 0; /* Adjust padding for list */
}

/* Basic header height variable for sticky sidebar */
:root {
  --header-height: 60px; /* Adjust as needed */
}

/* Add scrollbar styling similar to DesktopFilters component */
.scrollarea-hide-scrollbar::-webkit-scrollbar {
  width: 0px;
  background: transparent;
}

.scrollarea-hide-scrollbar {
  scrollbar-width: none; /* Firefox */
  -ms-overflow-style: none; /* IE and Edge */
}

:deep(.scrollbar) {
  opacity: 0;
  transition: opacity 0.2s ease;
  width: 6px !important;
  right: 2px !important;
}

:deep(.viewport):hover + .scrollbar,
:deep(.scrollbar:hover) {
  opacity: 1;
}

:deep(.scrollbar-thumb) {
  background-color: hsl(var(--muted-foreground) / 0.3) !important;
  border-radius: 6px !important;
  border: none !important;
}

:deep(.scrollbar-thumb:hover) {
  background-color: hsl(var(--muted-foreground) / 0.5) !important;
}

:deep(.dark .scrollbar-thumb) {
  background-color: hsl(var(--muted-foreground) / 0.4) !important;
}

:deep(.dark .scrollbar-thumb:hover) {
  background-color: hsl(var(--muted-foreground) / 0.6) !important;
}

/* Override the min-width applied by radix-vue ScrollArea */
:deep([data-radix-scroll-area-viewport]) > div {
  min-width: unset !important;
  width: 100% !important;
}

</style>
